import { useEffect, useState } from 'react'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { isTerminalCallPhase, patchVoiceCallSession } from '../stores/voice-call-store'
import { RETURN_TO_CALL_BANNER_Z_CLASS } from '../utils/call-overlay-layers'

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Persistent "Return to call" pill (green-bar style) rendered at the app-shell
 * level whenever a call session is live but its full-screen overlay has been
 * minimized. Tapping it re-presents the call overlay. This is the in-app
 * counterpart of iOS's green CallKit status pill / Android's ongoing-call
 * notification — the guarantee that a minimized call can always be reached.
 */
export function ReturnToCallBanner() {
  const session = useVoiceCallSession()
  const [now, setNow] = useState(Date.now())

  const active = Boolean(
    session &&
      !isTerminalCallPhase(session.phase) &&
      session.phase !== 'elsewhere' &&
      session.userMinimized,
  )

  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active])

  if (!session || !active) return null

  const label =
    session.phase === 'active' && session.connectedAt
      ? formatDuration(now - session.connectedAt)
      : session.phase === 'outgoing'
        ? 'Calling…'
        : session.phase === 'incoming'
          ? 'Incoming call'
          : 'Connecting…'
  const peerName = session.isGroup
    ? session.groupTitle ?? 'Group call'
    : // Unanswered urgent ring: the whole group is ringing — never one member.
      session.ringGroupName?.trim() || session.peer.displayName

  return (
    // z-[66] (RETURN_TO_CALL_BANNER_Z_CLASS): the minimized call overlay stays
    // MOUNTED at z-[60] with its chrome up to z-[65], so a banner below that is
    // unreachable — see utils/call-overlay-layers.ts for the full contract.
    <div
      className={`pointer-events-none fixed inset-x-0 top-[max(0.5rem,var(--safe-top))] ${RETURN_TO_CALL_BANNER_Z_CLASS} flex justify-center px-4`}
    >
      <button
        type="button"
        onClick={() => patchVoiceCallSession({ userMinimized: false })}
        className="pointer-events-auto flex max-w-full items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label={`Return to call with ${peerName}`}
      >
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
        </span>
        <span className="truncate">{peerName}</span>
        <span className="shrink-0 tabular-nums text-emerald-50/90">{label}</span>
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-emerald-50/90">
          Tap to return
        </span>
      </button>
    </div>
  )
}
